import { Box, Grid, styled } from '@mui/material';
import ActionLink from 'components/ActionLink';
import Button from 'components/Button';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { ActionRef } from 'further-types/lp/action';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { groupBy, mapValues, sumBy } from 'lodash';
import { useState } from 'react';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../hooks/useCapitalActionForm';
import useSyncInitialRefundValues from './hooks/useSyncInitialRefundValues';

const Toggle = styled(Button)({
  fontSize: '12px',
  padding: 0,
});

const actionsColumns = [
  {
    key: 'ledgerDate',
    label: 'Date',
  },
  {
    key: 'actionRef',
    label: 'Action',
    render: (row: { id: string; actionRef: ActionRef }) => (
      <ActionLink id={row.id} actionRef={row.actionRef} />
    ),
  },
  {
    key: 'allocatedAmountLcy',
    label: 'Allocated',
  },
  {
    key: 'paidAmountLcy',
    label: 'Funds paid in',
  },
  {
    key: 'overpaymentAmountLcy',
    label: 'Overpayment',
  },
];

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
};

const CapitalRefundDetails: React.FC<Props> = ({
  index,
  vehicleId,
  baseCurrency,
}) => {
  const [expandedRowId, setExpandedRowId] = useState<string>();

  const {
    formMethods: { watch },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const [overpaidActions] = api.capital.overpaidActions.useSuspenseQuery({
    vehicleId,
  });

  const commitmentValues = mapValues(
    groupBy(watch(`entries.${index}.capitalBreakdown`), 'commitmentId'),
    (value) => sumBy(value, 'amountLcy'),
  );

  useSyncInitialRefundValues({
    entryIndex: index,
    overpaidActions,
  });

  const handleToggleActions = (rowId: string) => {
    setExpandedRowId(rowId === expandedRowId ? undefined : rowId);
  };

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
    },
    {
      key: 'totalOverpaymentAmountLcy',
      label: 'Overpayment',
      render: (row) => {
        return (
          <Row spacing="sm" justify="space-between" fullWidth centered>
            {numberToCurrencyString(row.totalOverpaymentAmountLcy, {
              currency: baseCurrency,
            })}
            <Toggle variant="text" onClick={() => handleToggleActions(row.id)}>
              {row.id === expandedRowId ? 'Hide' : 'View'} overpaid transactions
            </Toggle>
          </Row>
        );
      },
    },
    {
      key: 'amountToReturnLcy',
      label: 'Amount to return',
      render: (row: { commitmentId: string }) => (
        <NumberInput
          disabled
          value={commitmentValues[row.commitmentId] ?? 0}
          decimals={2}
          placeholder="Enter amount"
          clearZeroValueOnFocus
        />
      ),
    },
  ];
  const rows = overpaidActions.map((action) => {
    return {
      id: action.commitmentId,
      commitmentId: action.commitmentId,
      investorName: action.investorName,
      totalOverpaymentAmountLcy: action.totalOverpaymentAmountLcy,
      actions: action.actions,
    };
  });

  return (
    <>
      <Grid container>
        <Grid size={{ sm: 12, md: 12, lg: 8 }}>
          <Box>
            <Table
              title="Return of capital"
              columns={columns}
              rows={rows}
              isExpandedRow={(row) => row.id === expandedRowId}
              getExpandedContent={(row) => (
                <Table
                  columns={actionsColumns}
                  rows={row.actions.map((action) => ({
                    id: action.id,
                    ledgerDate: action.ledgerDate.toLabel(),
                    actionRef: action.actionRef,
                    allocatedAmountLcy: numberToCurrencyString(
                      action.allocatedAmountLcy,
                      {
                        currency: baseCurrency,
                      },
                    ),
                    paidAmountLcy: numberToCurrencyString(
                      action.paidAmountLcy,
                      {
                        currency: baseCurrency,
                      },
                    ),
                    overpaymentAmountLcy: numberToCurrencyString(
                      action.overpaymentAmountLcy,
                      {
                        currency: baseCurrency,
                      },
                    ),
                  }))}
                />
              )}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default CapitalRefundDetails;
